import pricingData from '../pricing-data.json';
import type { TariffItem, TariffPeriodDays, Tariffs } from '../types/pricingApiTypes';
import type { PeriodDays, PlanId, PlanPrice, PlanWithPrices } from '../types';
import { numberFormat, ui } from '../constants';
import { fillTemplate, formatToman } from './index';

const plansMeta = pricingData.plans;

export const FREE_PLAN_ID = 'free' as const;
export const FREE_PLAN_PERIOD_DAYS = 7 as const;
export const FREE_PLAN_TRACKER_KEYWORDS = 10;
export const FREE_PLAN_TRACKER_FREQUENCY = 'weekly' as const;
export const FREE_PLAN_TRACKER_DEPTH = 5 as const;

export function isFreePlan(planId: PlanId | null | undefined): planId is typeof FREE_PLAN_ID {
  return planId === FREE_PLAN_ID;
}

export function hasFreePlanPrice(
  plans: Record<PlanId, PlanWithPrices>,
): boolean {
  return Boolean(plans[FREE_PLAN_ID]?.prices[FREE_PLAN_PERIOD_DAYS]);
}
const noPlanTariffNames = [
  pricingData.ui.noPlanOption.title,
  'بدون طرح',
] as const;

function normalizePlanName(name: string): string {
  return name
    .replace(/\u200c/g, '')
    .replace(/‌/g, '')
    .replace(/ي/g, 'ی')
    .replace(/\s+/g, ' ')
    .trim();
}

export function findTariffKey(
  planName: string,
  tariffs: Tariffs,
): string | undefined {
  const normalized = normalizePlanName(planName);
  return Object.keys(tariffs).find(
    (key) => normalizePlanName(key) === normalized,
  );
}

export function periodDaysToMonths(periodDays: PeriodDays): number {
  if (periodDays === FREE_PLAN_PERIOD_DAYS) return 0;
  return Math.round(periodDays / 30);
}

export function formatPlanSubscriptionLabel(periodDays: PeriodDays): string {
  if (periodDays === FREE_PLAN_PERIOD_DAYS) {
    return fillTemplate(ui.summary.subscriptionDaysTemplate, {
      period: numberFormat.format(periodDays),
    });
  }

  return fillTemplate(ui.summary.subscriptionTemplate, {
    period: numberFormat.format(periodDaysToMonths(periodDays) || 1),
  });
}

export function formatPlanPeriodSummaryLabel(periodDays: PeriodDays): string {
  if (periodDays === FREE_PLAN_PERIOD_DAYS) {
    return fillTemplate(ui.summary.periodDaysTemplate, {
      period: numberFormat.format(periodDays),
    });
  }

  return fillTemplate(ui.summary.periodTemplate, {
    period: numberFormat.format(periodDaysToMonths(periodDays)),
  });
}

export function formatPlanInvoiceItemLabel(
  planName: string,
  periodDays: PeriodDays,
): string {
  if (periodDays === FREE_PLAN_PERIOD_DAYS) {
    return fillTemplate(ui.invoice.planItemDaysTemplate, {
      planName,
      period: numberFormat.format(periodDays),
    });
  }

  return fillTemplate(ui.invoice.planItemTemplate, {
    planName,
    period: numberFormat.format(periodDaysToMonths(periodDays)),
  });
}

export function buildPlanPrice(tariff: TariffItem): PlanPrice {
  const hasDiscount = tariff.price > tariff.price_of;
  const finalLabel =
    tariff.price_of === 0 ? ui.common.free : formatToman(tariff.price_of);

  return {
    id: tariff.id,
    price: tariff.price_of,
    originalPrice: hasDiscount ? tariff.price : undefined,
    originalLabel: hasDiscount ? formatToman(tariff.price) : null,
    finalLabel,
    monthlyLabel: null,
  };
}

export function buildPlansWithPrices(
  tariffs: Tariffs,
): Record<PlanId, PlanWithPrices> {
  const result = {} as Record<PlanId, PlanWithPrices>;

  for (const [planId, meta] of Object.entries(plansMeta) as [
    PlanId,
    (typeof plansMeta)[PlanId],
  ][]) {
    const tariffKey = findTariffKey(meta.name, tariffs);
    const planTariffs = tariffKey ? tariffs[tariffKey] : undefined;
    const prices: Partial<Record<PeriodDays, PlanPrice>> = {};

    if (planTariffs) {
      for (const [days, item] of Object.entries(planTariffs)) {
        if (item) {
          prices[Number(days) as PeriodDays] = buildPlanPrice(item);
        }
      }
    }

    result[planId] = { ...meta, prices };
  }

  return result;
}

export function findNoPlanTariffKey(tariffs: Tariffs): string | undefined {
  for (const name of noPlanTariffNames) {
    const key = findTariffKey(name, tariffs);
    if (key) return key;
  }

  return undefined;
}

export function getNoPlanTariffPrice(
  tariffs: Tariffs,
  period: PeriodDays,
): PlanPrice | null {
  const tariffKey = findNoPlanTariffKey(tariffs);
  if (!tariffKey) return null;

  const item = tariffs[tariffKey][String(period) as TariffPeriodDays];
  return item ? buildPlanPrice(item) : null;
}

export function getNoPlanTariffPriceId(
  tariffs: Tariffs,
  period: PeriodDays,
): string | null {
  return getNoPlanTariffPrice(tariffs, period)?.id ?? null;
}

export function getPlanPrice(
  plans: Record<PlanId, PlanWithPrices>,
  planId: PlanId | null,
  period: PeriodDays,
): PlanPrice | null {
  if (!planId) return null;
  return plans[planId]?.prices[period] ?? null;
}
